import { useBodyScrollLock } from './useBodyScrollLock.svelte';

/**
 * Shared modal animation helper — encapsulates the closing state + reset logic
 * so modal consumers don't need a $effect for the closing-reset pattern.
 *
 * Replaces the repeated pattern across 5 modals:
 *   let closing = $state(false);
 *   $effect(() => { if ($modalOpen) closing = false; });
 *   function animateClose() { ... }
 *
 * Usage:
 *   const modal = useModalAnimation(() => $modalOpen, closeModal);
 *   // In template: class:closing={modal.closing} bind:this={modal.overlayEl}
 *   // To close: modal.animateClose()
 *
 * @param isOpen - Reactive getter for the modal's open state (e.g., () => $modalOpen)
 * @param onClose - Callback invoked after the close animation completes
 */
export function useModalAnimation(isOpen: () => boolean, onClose: () => void) {
  let closing = $state(false);
  let overlayEl = $state<HTMLDivElement | undefined>();

  useBodyScrollLock(isOpen);

  // Reset closing state when modal opens — centralizes the pattern
  // that was previously duplicated as an inline $effect in each modal
  // eslint-disable-next-line momentium/no-unguarded-effect -- modal animation lifecycle: must reset closing state when modal open state changes
  $effect(() => {
    if (isOpen()) closing = false;
  });

  function animateClose() {
    if (closing) return;
    closing = true;
    overlayEl?.addEventListener(
      'animationend',
      (e) => {
        if (e.target !== overlayEl) return;
        closing = false;
        onClose();
      },
      { once: true },
    );
  }

  return {
    get closing() {
      return closing;
    },
    get overlayEl() {
      return overlayEl;
    },
    set overlayEl(el: HTMLDivElement | undefined) {
      overlayEl = el;
    },
    animateClose,
  };
}
